import React, { useState, useEffect } from 'react'
import {
  Box,
  Card,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tabs,
  Tab,
  Typography,
} from '@mui/material'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { useAutoFetch } from 'src/v3/hooks/use-auto-fetch'
import { SearchIcon } from 'src/v3/core/icons'
import { selectUseCompliance } from 'src/v3/settings/settings.slice'
import {
  activeLinkedServiceChanged,
  fetchCredentialVerificationStatusesByLS,
  fetchLicenseVerificationStatusesByLS,
  fetchLinkedServices,
  fetchNREMTVerificationStatusesByLS,
  selectActiveLinkedService,
  selectCredentialVerificationsByLS,
  selectCredentialVerificationsByLSStatus,
  selectLicenseVerificationsByLS,
  selectLicenseVerificationsByLSStatus,
  selectLinkedServiceById,
  selectLinkedServices,
  selectLinkedServicesStatus,
  selectNREMTVerificationsByLS,
  selectNREMTVerificationsByLSStatus,
} from '../manage.slice'
import VerificationsDataGrid from './components/verifications-grid'
import ComplianceDashboard from './components/dashboard'

export default function Compliance() {
  let dispatch = useAppDispatch()

  let useCompliance = useAppSelector(selectUseCompliance)
  let linkedServices = useAppSelector(selectLinkedServices)
  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)
  let activeLinkedServiceID = useAppSelector(selectActiveLinkedService)
  let activeLinkedService = useAppSelector(
    selectLinkedServiceById(activeLinkedServiceID),
  ) || {
    nremtVerification: 'disabled',
    licenseVerification: 'disabled',
    credentialVerification: 'disabled',
  }

  let nremtVerifications = useAppSelector(
    selectNREMTVerificationsByLS(activeLinkedServiceID),
  )
  let nremtVerificationsStatus = useAppSelector(
    selectNREMTVerificationsByLSStatus(activeLinkedServiceID),
  )
  let licenseVerifications = useAppSelector(
    selectLicenseVerificationsByLS(activeLinkedServiceID),
  )
  let licenseVerificationsStatus = useAppSelector(
    selectLicenseVerificationsByLSStatus(activeLinkedServiceID),
  )
  let credentialVerifications = useAppSelector(
    selectCredentialVerificationsByLS(activeLinkedServiceID),
  )
  let credentialVerificationsStatus = useAppSelector(
    selectCredentialVerificationsByLSStatus(activeLinkedServiceID),
  )

  let { nremtVerification, licenseVerification, credentialVerification } =
    activeLinkedService

  let [verificationTab, setVerificationTab] = useState<
    'nremt' | 'license' | 'credential'
  >('nremt')

  useAutoFetch(linkedServicesStatus, fetchLinkedServices)

  useEffect(() => {
    if (linkedServicesStatus === 'fulfilled' && linkedServices?.length === 1) {
      dispatch(activeLinkedServiceChanged(linkedServices[0]?.id))
    }
  }, [linkedServicesStatus])

  useEffect(() => {
    if (
      activeLinkedServiceID !== 'unselected' &&
      nremtVerification === 'full' &&
      (!nremtVerificationsStatus ||
        nremtVerificationsStatus === 'uninitialized')
    ) {
      dispatch(fetchNREMTVerificationStatusesByLS(activeLinkedServiceID))
    }
  }, [activeLinkedServiceID, nremtVerificationsStatus])

  useEffect(() => {
    if (
      activeLinkedServiceID !== 'unselected' &&
      licenseVerification === 'full' &&
      (!licenseVerificationsStatus ||
        licenseVerificationsStatus === 'uninitialized')
    ) {
      dispatch(fetchLicenseVerificationStatusesByLS(activeLinkedServiceID))
    }
  }, [activeLinkedServiceID, licenseVerificationsStatus])

  useEffect(() => {
    if (
      activeLinkedServiceID !== 'unselected' &&
      credentialVerification === 'full' &&
      (!credentialVerificationsStatus ||
        credentialVerificationsStatus === 'uninitialized')
    ) {
      dispatch(fetchCredentialVerificationStatusesByLS(activeLinkedServiceID))
    }
  }, [activeLinkedServiceID, credentialVerificationsStatus])

  if (useCompliance) {
    return <ComplianceDashboard />
  }

  return (
    <Container maxWidth="xl" className="py-8">
      <Box className="mb-8">
        <Typography variant="h4" component="h1">
          Compliance
        </Typography>
        <Box sx={{ p: 0, mt: 4 }}>
          <FormControl fullWidth>
            <InputLabel>Select Linked Service</InputLabel>
            <Select
              value={activeLinkedServiceID}
              onChange={(e) =>
                dispatch(activeLinkedServiceChanged(e.target.value))
              }
              label="Select Linked Service"
              startAdornment={<SearchIcon sx={{ mr: 1 }} />}
            >
              <MenuItem value="unselected">Unselected</MenuItem>
              {linkedServices.map((service) => (
                <MenuItem key={service.id} value={service.id}>
                  {service.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Card sx={{ mt: 3, p: 3 }}>
          <Typography variant="h5" component="h2">
            Verification
          </Typography>
          <Tabs
            sx={{ mt: 1.5, mb: 0.5, ml: -2 }}
            value={verificationTab}
            onChange={(_, value) => setVerificationTab(value)}
          >
            <Tab value="nremt" label="NREMT" />
            <Tab value="license" label="Licenses & Certifications" />
            <Tab value="credential" label="Credentials" />
          </Tabs>
          {verificationTab === 'nremt' && (
            <>
              {nremtVerification === 'full' ? (
                <Box sx={{ m: -3, mt: 0 }}>
                  <VerificationsDataGrid
                    name="nremt"
                    verifications={nremtVerifications}
                    fetchVerifications={fetchNREMTVerificationStatusesByLS}
                  />
                </Box>
              ) : (
                <Typography sx={{ pt: 2 }}>
                  NREMT verification is not enabled for this linked service.
                </Typography>
              )}
            </>
          )}
          {verificationTab === 'license' && (
            <>
              {licenseVerification === 'full' ? (
                <Box sx={{ m: -3, mt: 0 }}>
                  <VerificationsDataGrid
                    name="license"
                    verifications={licenseVerifications}
                    fetchVerifications={fetchLicenseVerificationStatusesByLS}
                  />
                </Box>
              ) : (
                <Typography sx={{ pt: 2 }}>
                  License verification is not enabled for this linked service.
                </Typography>
              )}
            </>
          )}
          {verificationTab === 'credential' && (
            <>
              {credentialVerification === 'full' ? (
                <Box sx={{ m: -3, mt: 0 }}>
                  <VerificationsDataGrid
                    name="credential"
                    verifications={credentialVerifications}
                    fetchVerifications={fetchCredentialVerificationStatusesByLS}
                  />
                </Box>
              ) : (
                <Typography sx={{ pt: 2 }}>
                  Credential verification is not enabled for this linked
                  service.
                </Typography>
              )}
            </>
          )}
        </Card>
      </Box>
    </Container>
  )
}
